/**
 * 悬浮导航（godepth 同款：居中白色药丸、毛玻璃、激活项 sky→teal 渐变底）。
 *
 * 37 个页面装不进一条横栏，所以横栏上放五段（盘前/盘中/复盘/研究/系统），
 * 点开一段是那一段的页面竖向列表（图标磁贴 + 名字）。当前页所在的段常亮。
 * 向下滚动时收起、向上滚动时回来（godepth 行为），⌘K 随时可用。
 */
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Moon, Search, Sun } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTradingSession } from '@/providers';
import { useSettingsStore } from '@/stores/settings-store';
import { AITaskButton, AITaskPanel } from '@/components/ai/ai-task-panel';
import {
  HOME_PATH, NAV_ROUTES, SEGMENTS, SEGMENT_ICON, SEGMENT_LABEL, matchRoute, type Segment,
} from './registry';
import { useShellStore } from './shell-store';

function useSessionLabel(): { text: string; live: boolean } {
  const { session } = useTradingSession();
  if (session.isTradingHours) return { text: '交易中', live: true };
  switch (session.sessionType) {
    case 'lunch_break':
      return { text: '午休', live: false };
    case 'pre_market':
      return { text: '盘前', live: false };
    case 'after_hours':
      return { text: `已收盘 ${session.dataDate}`, live: false };
    default:
      return { text: `休市 ${session.lastTradingDay}`, live: false };
  }
}

/** 向下滚过 120px 收起，向上滚立即回来 */
function useHideOnScroll(): boolean {
  const [hidden, setHidden] = useState(false);
  const last = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > last.current && y > 120);
      last.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return hidden;
}

const PILL =
  'inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-xl px-3.5 text-sm font-medium transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40';

export function SegmentPanel({ seg, currentPath, onPick }: { seg: Segment; currentPath?: string; onPick?: () => void }) {
  const items = NAV_ROUTES.filter((r) => r.segment === seg);
  return (
    <div>
      <div className="flex items-baseline justify-between px-2 pb-2">
        <span className="text-sm font-semibold text-foreground">{SEGMENT_LABEL[seg]}</span>
        <span className="text-xs text-muted-foreground">{items.length} 个页面</span>
      </div>
      <div className="flex flex-col gap-0.5">
        {items.map((r) => {
          const on = r.path === currentPath;
          const Icon = r.icon;
          return (
            <Link
              key={r.path}
              to={r.path}
              onClick={onPick}
              aria-current={on ? 'page' : undefined}
              className={cn(
                'group flex items-center gap-3 rounded-xl px-2.5 py-1.5 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
                on ? 'bg-accent' : 'hover:bg-muted',
              )}
            >
              <span
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                  on
                    ? 'bg-gradient-to-br from-sky-500 to-teal-500 text-white shadow-sm'
                    : 'bg-gradient-to-br from-teal-50 to-sky-100 text-teal-600 dark:from-teal-500/15 dark:to-sky-500/15 dark:text-teal-300',
                )}
              >
                <Icon className="h-4 w-4" />
              </span>
              <span className={cn('truncate text-sm', on ? 'font-semibold text-accent-foreground' : 'font-medium text-foreground')}>
                {r.name}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function TopNav() {
  const { pathname } = useLocation();
  const current = matchRoute(pathname);
  const [open, setOpen] = useState<Segment | null>(null);
  const setPaletteOpen = useShellStore((s) => s.setPaletteOpen);
  const setLegacy = useShellStore((s) => s.setLegacy);
  const setTheme = useSettingsStore((s) => s.setTheme);
  const session = useSessionLabel();
  const hidden = useHideOnScroll() && open === null;
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  useEffect(() => setOpen(null), [pathname]);

  const toggleTheme = () => {
    const dark = document.documentElement.classList.contains('dark');
    setTheme(dark ? 'light' : 'dark');
  };

  return (
    <nav
      aria-label="主导航"
      className={cn(
        'fixed left-1/2 top-4 z-50 hidden -translate-x-1/2 transition-all duration-300 ease-out md:block',
        hidden && '-translate-y-24 opacity-0',
      )}
    >
      <div className="flex items-center gap-1 rounded-2xl border border-white/60 bg-white/80 px-2.5 py-2 shadow-float backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80">
        <Link
          to={HOME_PATH}
          aria-label="首页"
          className="mr-1 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-teal-500 text-white shadow-sm"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M3 17 L7 12 L11 14 L15 8 L21 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>

        {SEGMENTS.map((seg) => {
          const Icon = SEGMENT_ICON[seg];
          const active = current?.segment === seg;
          return (
            <Popover key={seg} open={open === seg} onOpenChange={(o) => setOpen(o ? seg : null)}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-current={active ? 'true' : undefined}
                  className={cn(
                    PILL,
                    active
                      ? 'bg-gradient-to-r from-sky-500/15 to-teal-500/15 text-sky-700 dark:text-sky-300'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white',
                    open === seg && !active && 'bg-slate-100 text-slate-900 dark:bg-white/5 dark:text-white',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {SEGMENT_LABEL[seg]}
                  {active && current && (
                    <span className="hidden max-w-[7rem] truncate font-normal text-sky-700/70 dark:text-sky-300/70 xl:inline">
                      · {current.name}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={12}
                className="max-h-[75vh] w-72 overflow-y-auto rounded-2xl border-border bg-popover/95 p-3 shadow-float backdrop-blur-xl"
              >
                <SegmentPanel seg={seg} currentPath={current?.path} onPick={() => setOpen(null)} />
                {seg === 'system' && (
                  <button
                    type="button"
                    onClick={() => setLegacy(true)}
                    className="mt-2 w-full rounded-lg px-2.5 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted"
                  >
                    切回旧版布局（回退对比用）
                  </button>
                )}
              </PopoverContent>
            </Popover>
          );
        })}

        <div className="mx-1.5 h-6 w-px bg-slate-200 dark:bg-white/10" />

        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className={cn(PILL, 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5')}
        >
          <Search className="h-4 w-4" />
          <span className="hidden lg:inline">搜索</span>
          <kbd className="hidden rounded-md border border-slate-200 bg-slate-50 px-1.5 font-mono text-[11px] text-slate-500 dark:border-white/10 dark:bg-white/5 lg:inline">
            {isMac ? '⌘K' : 'Ctrl K'}
          </kbd>
        </button>

        <span className="hidden items-center gap-1.5 whitespace-nowrap px-2 text-xs text-slate-500 dark:text-slate-400 xl:flex">
          <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', session.live ? 'animate-pulse bg-sky-500' : 'bg-slate-300')} />
          <span className="font-mono tabular-nums">{session.text}</span>
        </span>

        <AITaskButton />
        <button
          type="button"
          onClick={toggleTheme}
          aria-label="切换深浅主题"
          className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white"
        >
          <Sun className="hidden h-4 w-4 dark:block" />
          <Moon className="h-4 w-4 dark:hidden" />
        </button>
      </div>
      <AITaskPanel />
    </nav>
  );
}

/** 移动端顶部：标志 + 当前页名 + 搜索（段导航在底栏抽屉里） */
export function MobileTop() {
  const { pathname } = useLocation();
  const current = matchRoute(pathname);
  const setPaletteOpen = useShellStore((s) => s.setPaletteOpen);
  return (
    <header className="fixed inset-x-3 top-3 z-50 flex h-12 items-center gap-2 rounded-2xl border border-white/60 bg-white/85 px-2 shadow-float backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/85 md:hidden">
      <NavLink to={HOME_PATH} className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-teal-500 text-white">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M3 17 L7 12 L11 14 L15 8 L21 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </NavLink>
      <span className="flex-1 truncate text-sm font-semibold text-foreground">{current?.name ?? ''}</span>
      <button type="button" onClick={() => setPaletteOpen(true)} aria-label="搜索" className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500">
        <Search className="h-4 w-4" />
      </button>
    </header>
  );
}
